import type { PdSession } from 'src/app/core/services/admin-session-api.service';

/** Format seconds as `1h 5m 30s` (same rules as reports). */
export function formatDurationFromSeconds(totalSec: number | null | undefined): string {
  if (totalSec == null || Number.isNaN(Number(totalSec))) {
    return '—';
  }
  const sec = Math.max(0, Math.round(Number(totalSec)));
  if (sec === 0) {
    return '0s';
  }

  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;

  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
}

function parseLogEntryTimeMs(value: unknown, refDate: Date): number | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!t) return null;

  const iso = Date.parse(t);
  if (!Number.isNaN(iso)) return iso;

  const timePattern = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;
  const m = timePattern.exec(t);
  if (!m) return null;

  let hour = Number.parseInt(m[1] ?? '0', 10);
  const minute = Number.parseInt(m[2] ?? '0', 10);
  const second = m[3] ? Number.parseInt(m[3], 10) : 0;
  const ampm = m[4]?.toUpperCase();
  if (ampm === 'PM' && hour < 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;

  const d = new Date(refDate);
  d.setHours(hour, minute, second, 0);
  return d.getTime();
}

function durationMsFromConversationLog(log: unknown, refDate: Date): number | null {
  if (!Array.isArray(log) || log.length < 2) return null;
  const times: number[] = [];
  for (const entry of log) {
    if (!entry || typeof entry !== 'object') continue;
    const ms = parseLogEntryTimeMs((entry as { time?: unknown }).time, refDate);
    if (ms != null) times.push(ms);
  }
  if (times.length < 2) return null;
  const span = Math.max(...times) - Math.min(...times);
  return span > 0 ? span : null;
}

/** Runtime in seconds — matches reports API (`started_at`/`completed_at`, else transcript). */
export function computeSessionDurationSeconds(session: PdSession | null | undefined): number | null {
  if (!session) return null;

  if (session.started_at && session.completed_at) {
    const ms = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime();
    if (ms > 0) return Math.round(ms / 1000);
  }

  let log = session.conversation_log;
  if (typeof log === 'string') {
    try {
      log = JSON.parse(log);
    } catch {
      log = [];
    }
  }
  const ref = session.created_at ? new Date(session.created_at) : new Date();
  const fromLog = durationMsFromConversationLog(log, ref);
  if (fromLog != null && fromLog > 0) return Math.round(fromLog / 1000);

  return null;
}

export function formatSessionRuntime(session: PdSession | null | undefined): string {
  const sec = computeSessionDurationSeconds(session);
  if (sec == null) return '—';
  return formatDurationFromSeconds(sec);
}
